import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, ArrowUpToLine, ArrowRightToLine, Move3d, RefreshCcw, Grid3x3, Axis3d } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useViewSync } from '../../contexts/ViewSyncContext';
import useViewer3dModels from '../../hooks/useViewer3dModels';
import useViewer3dView, { COLOR_MODES } from '../../hooks/useViewer3dView';
import useViewer3dDatasets from '../../hooks/useViewer3dDatasets';
import { hasXyz } from '../../lib/dataset-points3d';
import { isScatterDataset } from '../../lib/raw-scatter3d';
import { useProjectTerrain } from '../../hooks/useTerrainSettings';
import ViewSyncToggle from '../ViewSyncToggle';
import Viewer3DScene from '../Viewer3DScene';
import TerrainControls3D from '../terrain/TerrainControls3D';
import { viewer3dPath } from '../../lib/map-session';

/**
 * Docked "3D view" panel of the map workspace (lib/panel-registry.js `viewer3d`): the same scene as
 * the /3d page (components/Viewer3DScene.js, hooks/useViewer3dModels.js) beside the map, for the
 * map's ?project= / ?dataset= / ?model=, with camera presets, colour mode and tickable x/y/z dataset point clouds and raw-survey apparent-resistivity scatters (hover tooltip included). Sync camera lives here only: with it on, the map centre
 * and the 3D orbit target follow each other (a map target far from the models fits the models).
 *
 * The Canvas is mounted only while the panel is on screen: closing it, or hiding it behind another
 * dock tab, unmounts it and releases its WebGL context.
 */
export default function Viewer3DPanel({ projectId = null, datasetId = null, modelId = null, navigate }) {
  const { C } = useTheme();
  const { syncCamera, setSyncCamera } = useViewSync();
  useProjectTerrain(projectId);
  const view = useViewer3dView();
  const { fenceModels, shownModels, storedLoading, loaded, total, datasets, fitModelIds } = useViewer3dModels({ projectId, datasetId, modelId });
  const { showGrid, setShowGrid, showAxes, setShowAxes, showFences, setShowFences, terrainReady, colorMode, setColorMode, onCameraPreset, showVolumePanel, setShowVolumePanel } = view;
  const { drawnIds, toggleDrawn, points, scatterIds, toggleScatter, scatter } = useViewer3dDatasets(datasets);
  // Hover text of the resistivity scatter point under the cursor: { text, x, y } | null
  const [scatterHover, setScatterHover] = useState(null);
  // Datasets with x/y/z columns (point cloud) and georeferenced raw surveys (apparent-resistivity scatter)
  const listed = useMemo(() => datasets.filter((d) => hasXyz(d) || isScatterDataset(d)), [datasets]);
  const drawnCount = listed.filter((d) => (hasXyz(d) && drawnIds.includes(d.id)) || (isScatterDataset(d) && scatterIds.includes(d.id))).length;

  // Sync only makes sense while this panel is open
  useEffect(() => () => setSyncCamera(false), [setSyncCamera]);

  // Dock tabs stay mounted behind the front one (display: none): draw the scene only when visible
  const hostRef = useRef(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setVisible(el.offsetWidth > 0 && el.offsetHeight > 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [showControls, setShowControls] = useState(false);
  const [showDatasets, setShowDatasets] = useState(false);
  const small = { fontSize: 11, color: C.text2 };

  return (
    <div data-testid="viewer3d-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: C.bg2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <ViewSyncToggle syncCamera={syncCamera} setSyncCamera={setSyncCamera} />
        <label style={{ ...small, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} data-testid="viewer3d-panel-models">
          <input type="checkbox" checked={showFences} onChange={(e) => setShowFences(e.target.checked)} />
          Models ({shownModels.length} of {fenceModels.length} drawn){storedLoading ? (total > 0 ? ` · Loading models ${Math.min(loaded + 1, total)} of ${total}…` : ' · Loading models…') : ''}
        </label>
        <button type="button" onClick={() => setShowControls((v) => !v)} aria-expanded={showControls}
          style={{ ...small, background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}>
          Terrain
        </button>
        <span style={{ display: 'flex', gap: 2 }} data-testid="viewer3d-panel-scene">
          {[['grid', 'Reference grid', Grid3x3, showGrid, setShowGrid], ['axes', 'XYZ axes', Axis3d, showAxes, setShowAxes]].map(([id, label, Icon, on, set]) => (
            <button key={id} type="button" title={label} aria-label={label} aria-pressed={on} data-testid={`viewer3d-panel-${id}`} onClick={() => set((v) => !v)}
              style={{ background: on ? C.bg4 : 'none', border: `1px solid ${on ? C.accent : C.border}`, borderRadius: 4, color: on ? C.text0 : C.text2, cursor: 'pointer', display: 'flex', padding: 2 }}>
              <Icon size={13} />
            </button>
          ))}
        </span>
        <span style={{ display: 'flex', gap: 2 }} data-testid="viewer3d-panel-presets">
          {[['top', 'Top', ArrowUpToLine], ['side', 'Side', ArrowRightToLine], ['perspective', 'Perspective', Move3d], ['reset', 'Reset view', RefreshCcw]].map(([id, label, Icon]) => (
            <button key={id} type="button" title={label} aria-label={label} onClick={() => onCameraPreset(id)}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, color: C.text2, cursor: 'pointer', display: 'flex', padding: 2 }}>
              <Icon size={13} />
            </button>
          ))}
        </span>
        <select value={colorMode} onChange={(e) => setColorMode(e.target.value)} aria-label="Colour mode"
          style={{ ...small, background: C.bg4, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 2px', maxWidth: 110 }}>
          {COLOR_MODES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button type="button" onClick={() => setShowVolumePanel((v) => !v)} aria-expanded={showVolumePanel} title="Volume tools: render mode, visible range, volume, XYZ export"
          style={{ ...small, background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}>
          Volume
        </button>
        <button type="button" onClick={() => setShowDatasets((v) => !v)} aria-expanded={showDatasets}
          style={{ ...small, background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}>
          Datasets{drawnCount ? ` (${drawnCount})` : ''}
        </button>
        <button type="button" title="Open the full 3D viewer page" aria-label="Open the full 3D viewer page"
          onClick={() => navigate?.(viewer3dPath(projectId, { dataset: datasetId, model: modelId }))}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.text2, cursor: 'pointer', display: 'flex', padding: 2 }}>
          <ExternalLink size={13} />
        </button>
      </div>
      {showDatasets && (
        <div data-testid="viewer3d-panel-datasets" style={{ padding: 8, borderBottom: `1px solid ${C.border}`, flexShrink: 0, maxHeight: '30%', overflow: 'auto' }}>
          {listed.length === 0 && <div style={small}>No datasets with x/y/z columns or georeferenced raw surveys</div>}
          {listed.map((d) => (
            <div key={d.id} style={{ marginBottom: 3 }}>
              {hasXyz(d) && (
                <label style={{ ...small, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Draw as a point cloud">
                  <input type="checkbox" checked={drawnIds.includes(d.id)} onChange={() => toggleDrawn(d.id)} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || d.filename || d.id}</span>
                  {points.errors[d.id] && (
                    <span role="alert" title={points.errors[d.id]} style={{ color: '#f87171', flexShrink: 0 }}>✗ could not load points: {points.errors[d.id]}</span>
                  )}
                </label>
              )}
              {isScatterDataset(d) && (
                <label style={{ ...small, display: 'flex', alignItems: 'center', gap: 6, paddingLeft: hasXyz(d) ? 16 : 0, cursor: 'pointer' }}
                  title="One point per reading at its pseudo-position, coloured by apparent resistivity">
                  <input type="checkbox" checked={scatterIds.includes(d.id)} onChange={() => toggleScatter(d.id)} />
                  {!hasXyz(d) && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || d.filename || d.id}:</span>}
                  <span>Apparent resistivity scatter</span>
                  {scatter.errors[d.id] && (
                    <span role="alert" title={scatter.errors[d.id]} style={{ color: '#f87171', flexShrink: 0 }}>✗ {scatter.errors[d.id]}</span>
                  )}
                </label>
              )}
            </div>
          ))}
        </div>
      )}
      {showControls && (
        <div style={{ padding: 8, borderBottom: `1px solid ${C.border}`, flexShrink: 0, maxHeight: '40%', overflow: 'auto' }}>
          <TerrainControls3D C={C} ready={terrainReady} />
        </div>
      )}
      <div ref={hostRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {visible ? (
          <Viewer3DScene view={view} datasets={datasets} drawnIds={drawnIds} points={points} scatterIds={scatterIds} scatter={scatter}
            onScatterHover={setScatterHover} fenceModels={fenceModels} fitModelIds={fitModelIds} />
        ) : null}
      </div>
      {scatterHover && (
        <div data-testid="viewer3d-scatter-tooltip"
          style={{ position: 'fixed', left: scatterHover.x + 12, top: scatterHover.y + 12, zIndex: 50, pointerEvents: 'none', fontSize: 11, padding: '3px 8px', borderRadius: 4, background: C.bg2, border: `1px solid ${C.border}`, color: C.text0 }}>
          {scatterHover.text}
        </div>
      )}
    </div>
  );
}
